import React, { useEffect, useMemo, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ShipeeziNewGrid, { Column, Icons as GridIcons } from "Components/AppGrid/shipeezi-new-grid";
import axios, { AxiosError } from "axios";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Button } from "../../../../../components/Form";
import { Color } from "../../../../../components/Form/Button";
import BadgeStatus from "../../../../../components/Generic/BadgeStatus";
import renderSharedReference from "../../../../../components/Generic/SharedReference";
import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../../../../../constants";
import useActionSupplier from "../../../../../hooks/useActionSupplier";
import { Error } from "../../../../../models/error";
import { Location } from "../../../../../models/location";
import { Searches, Filters } from "../../../../../models/shipeezi.table";
import { Supplier, SupplierIds } from "../../../../../models/supplier";
import SupplierService from "../../../../../services/supplier.service";
import { Status, UserStatus } from "../../../../../util/enums";
import { SupplierKeys } from "../../../../../util/queries";
import InviteSupplierModal, { InviteSupplierModalHandles } from "./InviteSupplierModal";
import useStyles from "./styles";
import { useAppState } from "Context/StateContext";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceName, ResourceCanEnum } from "Util/enums/accessRule";

const SupplierScreen = () => {
  const { t: translate } = useTranslation();
  const { getAllSuppliers, countSuppliers } = useActionSupplier();
  const classes = useStyles();
  const history = useHistory();
  const queryClient = useQueryClient();
  const inviteModalRef = useRef<InviteSupplierModalHandles>(null);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [rowsSelected, setRowsSelected] = useState<string[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const {
    data: suppliers,
    isFetching: loadingSuppliers,
    refetch: refetchSuppliers,
  } = getAllSuppliers(page, pageSize, searches, filters);

  const { currentUser, userPermissions } = useAppState();
  const [supplierPermission, setSupplierPermission] = useState<{
    view: boolean,
    edit: boolean,
    create: boolean,
  }>({
    view: false,
    edit: false,
    create: false,
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setSupplierPermission({
        view: hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_SUPPLIERS_VIEW),
        edit: checkResourceCan(userPermissions, ResourceName.SUPPLIERS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_SUPPLIERS_EDIT),
        create: checkResourceCan(userPermissions, ResourceName.SUPPLIERS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_SUPPLIERS_ADD_NEW),
      })
    }
  }, [userPermissions])

  const rowsSelectedIsAvailableToInvite = useMemo(() => {
    const allRowsHaveNameAndEmailAndNotIsCompany = rowsSelected.every((rowId) => {
      const supplier = suppliers?.find((supplier) => Number(rowId) === Number(supplier.id));

      if (!supplier) return true;

      if (!supplier.name) return false;
      if (!supplier.email) return false;

      const find = suppliers?.find(
        (supplier) =>
          Number(supplier.id) === Number(rowId) &&
          (!supplier?.associatedCompany ||
            supplier?.associatedCompany?.status !== UserStatus.ACTIVE)
      );

      if (!find) return false;

      return true;
    });

    return allRowsHaveNameAndEmailAndNotIsCompany;
  }, [rowsSelected, suppliers]);

  const { mutate: updateSupplier } = useMutation({
    mutationKey: SupplierKeys.update(),
    mutationFn: (supplierInfos: { id: number; supplier: Supplier }) =>
      SupplierService.update(supplierInfos.id, supplierInfos.supplier),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      refetchSuppliers();

      queryClient.invalidateQueries({ queryKey: [SupplierKeys.all] });
    },
    onError: (error: AxiosError<Error>) => {
      if (axios.isAxiosError(error) && error.response?.data && error.response.data?.email) {
        NotificationToast.error(error.response.data?.email);
      } else {
        NotificationToast.error(translate("anErrorOccuredTryAgain"));
      }
    },
  });
  const { isFetching: loadingSuppliersCount } = countSuppliers(setTotalItems, searches, filters);

  const handleCurrentPage = (numPage: number) => {
    document.location.href = "#top";
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSearch = (filters: Searches, _: string) => {
    setSearches(filters);
  };

  const renderNameTagIfSupplierIsCompanyActive = (value?: string, supplier?: Supplier) => {
    if (
      !value ||
      !supplier ||
      !supplier.associatedCompany ||
      supplier.associatedCompany.status !== Status.ACTIVE
    )
      return value;

    return renderSharedReference(value, true, translate("thisSupplierIsPartOfShipeezi"), false);
  };

  const columns: Column[] = [
    {
      name: "name",
      title: translate("name"),
      formatComponent: renderNameTagIfSupplierIsCompanyActive,
    },
    { name: "contactName", title: translate("mainContact"), searchIdentifier: "contact_name" },
    { name: "email", title: translate("email") },
    {
      name: "location",
      title: translate("location"),
      formatText: (location: Location) => location?.longAddress,
      hideSearch: true,
    },
    {
      name: "createdAt",
      title: translate("createdAt"),
      formatText: (value: string) => (value ? format(new Date(value), "dd/MM/yyyy") : null),
      hideSearch: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (value: UserStatus, supplier: Supplier) => {
        let status = value;

        if (status === UserStatus.INVITATION_SENT && supplier.expireInvitationDate) {
          const now = new Date();
          const isExpired = new Date(supplier.expireInvitationDate).getTime() <= now.getTime();

          if (isExpired) status = UserStatus.INVITE_EXPIRED;
        }

        return <BadgeStatus value={status} className={classes.noWhiteSpace} />;
      },
      hideSearch: true,
    },
  ];

  const redirectSupplier = (supplierId: number | string, viewOnly = false) => {
    if (supplierId) {
      history.push({
        pathname: `${PRIVATE_ROUTES.SUPPLIERS_MNT}/${supplierId}`,
        state: { viewOnly, supplierId },
      });
    }
  };

  const changeStatus = (id: number | string, supplier: Supplier) => {
    if (id)
      updateSupplier({
        id: Number(id),
        supplier: {
          status: supplier.status === UserStatus.INACTIVE ? Status.ACTIVE : UserStatus.INACTIVE,
        } as Supplier,
      });
  };

  const onCallInviteSupplierModal = (supplierIds: SupplierIds) => {
    inviteModalRef?.current?.open(supplierIds);
  };

  const renderInviteSupplierButtonByRow = (supplier: Supplier) => {
    if (
      !supplier ||
      (supplier &&
        supplier.associatedCompany &&
        supplier.associatedCompany.status === UserStatus.ACTIVE)
    ) {
      return null;
    }

    if (!supplier.email) {
      return (
        <Tooltip
          data-cy="invite-supplier"
          title={translate("thisSupplierDoesNotHaveAnEmailToSendInvitation")}
          placement="top"
        >
          <div>
            <GridIcons.email color="#D0D5DD" size={18} />
          </div>
        </Tooltip>
      );
    }

    if (supplier.status === UserStatus.INACTIVE) {
      return (
        <Tooltip
          data-cy="invite-supplier"
          title={translate("youCannotInviteInactiveSuppliers")}
          placement="top"
        >
          <div>
            <GridIcons.email color="#D0D5DD" size={18} />
          </div>
        </Tooltip>
      );
    }

    return (
      <Tooltip data-cy="invite-supplier" title={translate("inviteSupplier")} placement="top">
        <div>
          <GridIcons.email
            onClick={() => onCallInviteSupplierModal([Number(supplier.id)])}
            color="#475467"
            className={classes.button}
            size={18}
          />
        </div>
      </Tooltip>
    );
  };

  const renderInviteSuppliersButton = () => {
    if (!rowsSelected.length) return null;

    const tooltip = !rowsSelectedIsAvailableToInvite
      ? translate("allSelectedSuppliersMustHaveNameEmailAndNotYetBelongToShipeezi")
      : "";

    return (
      <Tooltip title={tooltip}>
        <div>
          <Button
            data-cy="button-invite"
            customColor={Color.INFO}
            tooltip={`${translate("invite")} ${translate("supplier")}`}
            disabled={!rowsSelectedIsAvailableToInvite}
            onClick={() => onCallInviteSupplierModal(rowsSelected.map((row) => Number(row)))}
          >
            <div className={classes.buttonInvite}>
              <GridIcons.email size={18} color="#3F6280" />
              <span>{translate("inviteSuppliers")}</span>
            </div>
          </Button>
        </div>
      </Tooltip>
    );
  };

  return (
    <>
      <a id="top" style={{ position: "absolute", top: "-100px" }}></a>
      <div className={classes.suppliersGridWrapper}>
        <ShipeeziNewGrid
          data={suppliers ?? []}
          totalItems={totalItems}
          loadingData={loadingSuppliers || loadingSuppliersCount}
          namePage={translate("suppliers")}
          emptyData={
            loadingSuppliers ? translate("loadingSuppliers") : translate("noInformationToBeShown")
          }
          columns={columns}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          onChangePageSize={handlePageSize}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          generalActions={{
            orderBy: {
              left: ["handleSearch", "handleFilterBy"],
              right: ["inviteSuppliers", "handleNewRecord"],
            },
            inviteSuppliers: renderInviteSuppliersButton(),
            handleNewRecord: isShowAction(currentUser, supplierPermission.create)
              ? {
                text: translate("create"),
                action: () =>
                  history.push({
                    pathname: PRIVATE_ROUTES.SUPPLIERS_MNT,
                    state: {
                      viewOnly: false,
                    },
                  }),
                tooltip: translate("createSupplier"),
              }
              : undefined,
            handleFilterBy: {
              categories: [
                { name: translate("name"), column: "name" },
                { name: translate("email"), column: "email" },
                { name: translate("mainContact"), column: "contactName" },
                {
                  name: translate("status"),
                  column: "status",
                  options: [
                    UserStatus.ACTIVE,
                    UserStatus.INACTIVE,
                    UserStatus.INVITATION_SENT,
                    UserStatus.INVITE_EXPIRED,
                    UserStatus.REJECTED,
                  ],
                },
              ],
              onChange: handleFilter,
            },
            handleSearch: {
              inputPlaceholder: translate("typePressEnter"),
              inputSearchMode: "enter",
              onSearch: (filters, value) => handleSearch(filters, value),
            },
          }}
          rowActions={{
            orderBy: ["handleEdit", "handleDetails", "handleChange", "inviteSupplier"],
            handleEdit: isShowAction(currentUser, supplierPermission.edit)
              ? (rowId) => redirectSupplier(rowId)
              : undefined,
            handleDetails: isShowAction(currentUser, supplierPermission.view)
              ? (rowId) => redirectSupplier(rowId, true)
              : undefined,
            handleChange: (rowId, row) => changeStatus(rowId, row),
          }}
          rowActionsExtra={[
            {
              title: translate("inviteSupplier"),
              key: "inviteSupplier",
              buttonAndAction: (_, row: Supplier) => renderInviteSupplierButtonByRow(row),
            },
          ]}
          positionActionsColumn="right"
          selecteds={rowsSelected}
          handleSelect={setRowsSelected}
        />
        <InviteSupplierModal ref={inviteModalRef} onSuccess={() => refetchSuppliers()} />
      </div>
    </>
  );
};

export default SupplierScreen;
